/* eslint-disable react-hooks/exhaustive-deps */
/**
 *
 * Login
 *
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { routes } from 'utils/routes';
import { useLoginSlice } from './slice';
import { selectLogin } from './slice/selectors';
import './styles.scss';
import { useQuery, validateEmail } from 'utils/commons';
import { toast } from 'react-toast';
import { useFormValidation } from '../../hooks/useFormValidation';
import api, { withAuthConfig } from 'utils/api';
import { urls } from 'utils/apiUrls';
import { useRecaptcha } from '../../hooks/useRecaptcha';

interface Props {}
interface LoginFormErrors {
  email?: string;
  password?: string;
}

export function Login(props: Props) {
  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState('');
  const [password, setPassword] = useState('');
  const [securityLoginNeeded, setSecurityLoginNeeded] = useState(false);
  const [isResendVerifyVisible, setIsResendVerifyVisible] = useState(false);
  const [show2FAStep, setShow2FAStep] = useState(false);
  const [twoFAToken, setTwoFAToken] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { token: recaptchaToken, reset: resetRecaptcha } = useRecaptcha(
    'login-recaptcha',
  );

  const { getFieldProps, getFieldError } = useFormValidation();

  const validateLoginForm = () => {
    const errors: LoginFormErrors = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      errors.email = 'Invalid email format';
    }

    if (!password) {
      errors.password = 'Password is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };
  const [
    isForgotPasswordFormVisible,
    setIsForgotPasswordFormVisible,
  ] = useState(false);

  const [verifiedUser, setVerifiedUser] = useState(false);
  const [isAppsumoEmail, setIsAppsumoEmail] = useState(false);
  const [isAppsumoPurchase, setIsAppsumoPurchase] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [passwordResetExpired, setPasswordResetExpired] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const itemsPerView = 3;

  const loginPanelItems = [
    { icon: '🔒', title: 'Two-Factor Authentication', description: 'Add an extra layer of security with authenticator apps or backup codes.' },
    { icon: '🤖', title: 'Clause Drafting Assistant', description: 'Your smart helper reviews documents and makes sure every detail is covered.' },
    { icon: '⚙️', title: 'Custom Workflows', description: 'Build approval chains, internal signing, and multi-party workflows.' },
  ];

  const { actions } = useLoginSlice();
  const loginState = useSelector(selectLogin);
  const dispatch = useDispatch();

  const params = useQuery();
  const history = useHistory();

  useEffect(() => {
    return () => {
      dispatch(actions.resetLoginError());
    };
  }, []);

  useEffect(() => {
    if (params.get('errorText')) {
      dispatch(actions.loginError({ message: params.get('errorText') }));
      params.delete('errorText');
    }
    if (
      window.location.href.includes('guest') &&
      params.get('return_to') !== null
    ) {
      history.push(decodeURIComponent(params.get('return_to') as string));
    } else if (params.get('return_to')) {
      setSecurityLoginNeeded(true);
    }

    if (params.get('verified')) {
      setVerifiedUser(true);
    }

    if (params.get('passwordResetSent')) {
      setPasswordResetSent(true);
      // Clean up URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('passwordResetSent');
      window.history.replaceState({}, '', newUrl.toString());
    }

    if (params.get('passwordResetSuccess')) {
      setPasswordResetSuccess(true);
      // Clean up URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('passwordResetSuccess');
      window.history.replaceState({}, '', newUrl.toString());
    }

    if (params.get('passwordResetExpired')) {
      setPasswordResetExpired(true);
      // Clean up URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('passwordResetExpired');
      window.history.replaceState({}, '', newUrl.toString());
    }

    const isAppSumo = params.get('appSumo');
    const queryEmail = params.get('email');

    if (isAppSumo) {
      if (window.location.href.indexOf('login?email=')) {
        setIsAppsumoPurchase(true);
      }
      setIsAppsumoEmail(true);
    } else {
      setIsAppsumoEmail(false);
    }

    if (queryEmail) {
      updateEmail(queryEmail);
      setEmail(queryEmail || '');
    }
  }, [params]);

  const login = e => {
    e.preventDefault();
    const { isValid, errors } = validateLoginForm();
    if (!isValid) {
      Object.values(errors)
        .filter((error): error is string => error !== undefined)
        .forEach(error => {
          toast.error(error);
        });
      return;
    }
    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification.');
      return;
    }
    dispatch(
      actions.login({
        request: {
          email,
          password,
          recaptchaToken,
        },
        verifyCb: () => {
          setIsResendVerifyVisible(true);
        },
        setPasswordCb: () => {
          history.push(
            `${routes.newAccount}?email=${encodeURIComponent(email)}`,
          );
        },
        requires2FACb: () => {
          setShow2FAStep(true);
        },
        successCb: user => {
          if (isAppsumoEmail) {
            toast.success(
              'Congratulation, your account has been upgraded successfully.',
            );
          }
        },
        errorCb: () => {
          resetRecaptcha();
        },
      }),
    );
  };

  const requestPasswordReset = e => {
    e.preventDefault();
    dispatch(
      actions.requestPasswordReset({
        request: {
          email,
        },
        successCb: () => {
          setIsForgotPasswordFormVisible(false);
          history.push(`${routes.login}?passwordResetSent=true`);
        },
      }),
    );
  };

  const updateEmail = email => {
    const emailStateIs = validateEmail(email);
    if (emailStateIs) {
      setEmail(email);
      setValidEmail('true');
    } else {
      setEmail(email);
    }
  };

  useEffect(() => {
    if (loginState.isLoggedIn) {
      // // console.log(loginState.user);
      if (loginState.user?.email?.includes('sandbox')) {
        localStorage.setItem('sandBox', 'true');
      } else {
        localStorage.removeItem('sandBox');
      }

      if (loginState.user.profileCompletion) {
        const returnTo = params.get('return_to');
        const redirect = params.get('redirect');
        const docId = params.get('docId');
        const accessKey = params.get('accessKey');

        if (redirect === 'document-import' && docId && accessKey) {
          history.push(
            `${routes.documentImport}?docId=${docId}&accessKey=${accessKey}`,
          );
        } else if (returnTo) {
          history.push(returnTo);
        } else if (loginState.user.primaryCompany)
          history.push(routes.companyDashboard(loginState.user.primaryCompany));
        else history.push(routes.dashboard);
      } else {
        history.push(routes.completeAccount(loginState.user.id));
      }
    }
  }, [loginState.isLoggedIn]);

  const onResendVerify = () => {
    dispatch(
      actions.doResendVerifyLink({
        email,
      }),
    );
  };

  const verify2FA = e => {
    e.preventDefault();
    if (!twoFAToken) return;

    dispatch(
      actions.verify2FAStart({
        request: {
          email,
          token: twoFAToken,
          useBackupCode,
        },
        successCb: user => {
          if (isAppsumoEmail) {
            toast.success(
              'Congratulation, your account has been upgraded successfully.',
            );
          }
        },
      }),
    );
  };

  const changelogItems = [
    {
      icon: '🔒',
      title: 'Two-Factor Authentication',
      tag: 'Security',
      description:
        'Add an extra layer of security to your account. Use an authenticator app or backup codes to keep your login safe and protected.',
    },
    {
      icon: '🤖',
      title: 'Clause Drafting Assistant',
      tag: 'Smart Help',
      description:
        'Your smart helper reviews documents, checks fields, and makes sure every detail is covered.',
    },
    {
      icon: '⚙️',
      title: 'Custom Fields',
      tag: 'Customization',
      description:
        'Design fields that fit your workflow. Add your own types, labels, and instructions to capture the exact data you need.',
    },
    {
      icon: '📋',
      title: 'Flexible Field Display',
      tag: 'Workflow',
      description:
        'Choose how recipients complete fields: guided forms before PDF or direct document filling for classic signing.',
    },
    {
      icon: '🔐',
      title: 'Lockable Fields',
      tag: 'Protection',
      description:
        'Keep important fields safe from accidental edits. Lock pre-filled data while allowing other fields to be updated.',
    },
  ];

  const maxSlides =
    changelogItems.length <= itemsPerView
      ? 1
      : changelogItems.length - itemsPerView + 1;

  useEffect(() => {
    if (maxSlides > 1 && !isHovered) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % maxSlides);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [maxSlides, isHovered]);

  const nextSlide = () => {
    if (currentSlide < maxSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const goToSlide = index => {
    setCurrentSlide(index);
  };

  return (
    <div className="login-wrapper has-panel">
      <div className="login-sales">
        <div className="sales-content">
          <h2>Sign contracts<br/>with confidence.</h2>
          <p className="sales-subtitle">Trusted by teams managing real contracts.</p>
          {loginPanelItems.map((item, i) => (
            <div className="sales-item" key={i}>
              <div className="sales-icon">{item.icon}</div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </div>
          ))}
          <div className="sales-stats">
            <div className="sales-stat">
              <span className="sales-stat-number">2,940</span>
              <span className="sales-stat-label">Company Accounts</span>
            </div>
            <div className="sales-stat-divider"></div>
            <div className="sales-stat">
              <span className="sales-stat-number">29,644</span>
              <span className="sales-stat-label">Documents Signed</span>
            </div>
          </div>
        </div>
      </div>
      <div className="login-form-container">
        <div className="formHolder">
          <div className="loginBox p-lg">
            {loginState.resendVerifyLinkSuccess ? (
              <div className="align-items-align-center">
                <img
                  width={100}
                  src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/animated-icon-email-success.gif"
                  className="successIcon"
                  alt=""
                />

                <h2 className="successMessage">Success</h2>
                <p>A verification code has been emailed to</p>
                <code>{email}</code>
                <div className="transitionedInputWrapper mb-17">
                  <div className="text-info mt-20">
                    Please open the email you have received and follow the
                    instructions provided to complete the setup process for your
                    account.
                  </div>
                </div>
              </div>
            ) : (
              <>
                {securityLoginNeeded ? (
                  <img
                    width={90}
                    className="lockIcon"
                    src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/lock.png"
                    alt=""
                  />
                ) : verifiedUser ? (
                  <div className="logoIMG">
                    <img
                      width={90}
                      src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/animated-logo-huge.gif"
                      alt=""
                    />
                  </div>
                ) : (
                  <div className="logoIMG">
                    <img
                      width={90}
                      src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/animated-logo-huge.gif"
                      alt=""
                    />
                  </div>
                )}

                {isResendVerifyVisible ? (
                  <div className="title">Ooops</div>
                ) : !isResendVerifyVisible && securityLoginNeeded ? (
                  <>
                    <div className="title">Security Check!!!</div>
                  </>
                ) : verifiedUser ? (
                  <>
                    {' '}
                    <div className="title">Thank You!</div>
                  </>
                ) : isAppsumoPurchase ? (
                  <>
                    <div className="title">👋 Welcome Sumo-ling!</div>
                  </>
                ) : isForgotPasswordFormVisible ? (
                  <div className="title">Reset Password</div>
                ) : show2FAStep ? (
                  <div className="title">Two-Factor Authentication</div>
                ) : (
                  <div className="title">Welcome Back</div>
                )}

                {!isResendVerifyVisible &&
                  !securityLoginNeeded &&
                  !isAppsumoPurchase &&
                  !verifiedUser &&
                  !isForgotPasswordFormVisible &&
                  !show2FAStep && (
                    <>
                      <div className="subTitle">Sign in to your workspace</div>
                    </>
                  )}
                {show2FAStep && (
                  <div className="subTitle">
                    {useBackupCode
                      ? 'Enter one of your backup codes'
                      : 'Enter the 6-digit code from your authenticator app'}
                  </div>
                )}
                {verifiedUser && (
                  <>
                    <div className="transitionedInputWrapper mb-17">
                      <div className="password-reset-success">
                        <i className="fi fi-rr-check-circle"></i>
                        <p>
                          Your email has been successfully verified. For
                          security purposes, please log in to continue setting
                          up your account{' '}
                        </p>
                      </div>
                    </div>
                  </>
                )}
                {isAppsumoPurchase && (
                  <>
                    <div className="subTitle">
                      Please login below to complete
                      <br />
                      your AppSumo purchase.
                    </div>
                  </>
                )}
                {!isResendVerifyVisible &&
                  securityLoginNeeded &&
                  !isForgotPasswordFormVisible && (
                    <>
                      <div className="subTitle">
                        To view this document please login.
                      </div>
                    </>
                  )}
                <form
                  className="d-flex flex-column login-form"
                  onSubmit={show2FAStep ? verify2FA : login}
                >
                  {!isResendVerifyVisible &&
                    !isForgotPasswordFormVisible &&
                    !show2FAStep && (
                      <>
                        <div className="transitionedInputWrapper">
                          <label className="inp">
                            <input
                              required
                              type="email"
                              placeholder="&nbsp;"
                              disabled={isAppsumoEmail}
                              tabIndex={1}
                              {...getFieldProps('email', {
                                required: true,
                                email: true,
                              })}
                              onChange={e => {
                                updateEmail(e.target.value);
                                getFieldProps('email', {
                                  required: true,
                                  email: true,
                                }).onChange(e);
                              }}
                            />
                            <span className="label">Email</span>
                            <span className="focus-bg"></span>
                          </label>
                          {getFieldError('email') && (
                            <div className="form-error-message">
                              {getFieldError('email')}
                            </div>
                          )}
                        </div>

                        <div className="transitionedInputWrapper">
                          <label className="inp password-field-container">
                            <input
                              required
                              type={showPassword ? 'text' : 'password'}
                              autoComplete="current-password"
                              placeholder="&nbsp;"
                              tabIndex={2}
                              {...getFieldProps('password', {
                                required: true,
                                password: true,
                              })}
                              onChange={e => {
                                setPassword(e.target.value);
                                getFieldProps('password', {
                                  required: true,
                                  password: true,
                                }).onChange(e);
                              }}
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              className="password-toggle"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              <i className={showPassword ? 'fi fi-rr-eye-crossed' : 'fi fi-rr-eye'}></i>
                            </button>
                            <span className="label">Password</span>
                            <span className="focus-bg"></span>
                          </label>
                          {getFieldError('password') && (
                            <div className="form-error-message">
                              {getFieldError('password')}
                            </div>
                          )}
                        </div>

                        <div
                          className="mt-10 w-100 transitionedInputWrapper d-flex justify-content-center align-items-center"
                          style={{ flexDirection: 'column', gap: '12px' }}
                        >
                          <div id="login-recaptcha" className="w-100 mt-10"></div>
                          <button
                            type="submit"
                            tabIndex={3}
                            className={`full-width mb-17 mt-20 primaryButton large-btn bold loginButton ' ${
                              loginState.isLoggingIn ? 'roundSpinnerButton' : ''
                            }`}
                            onClick={() => {
                              setIsResendVerifyVisible(false);
                              actions.resetLoginError();
                            }}
                            disabled={
                              loginState.isLoggingIn ||
                              email === '' ||
                              password === '' ||
                              !validEmail ||
                              !recaptchaToken
                            }
                          >
                            {!loginState.isLoggingIn ? (
                              'Sign in'
                            ) : (
                              <i className="gg-spinner-two"></i>
                            )}
                          </button>
                        </div>
                      </>
                    )}

                  {show2FAStep && (
                    <>
                      <div className="transitionedInputWrapper">
                        <label className="inp">
                          <input
                            type="text"
                            placeholder="&nbsp;"
                            value={twoFAToken}
                            onChange={e => setTwoFAToken(e.target.value)}
                            maxLength={useBackupCode ? 8 : 6}
                            autoComplete="off"
                            tabIndex={1}
                          />
                          <span className="label">
                            {useBackupCode
                              ? 'Backup Code'
                              : 'Authentication Code'}
                          </span>
                          <span className="focus-bg"></span>
                        </label>
                      </div>

                      <div className="transitionedInputWrapper d-flex justify-content-center align-items-center">
                        <button
                          type="submit"
                          tabIndex={2}
                          className={`full-width mb-17 mt-20 primaryButton large-btn bold ${
                            loginState.isVerifying2FA
                              ? 'roundSpinnerButton'
                              : ''
                          }`}
                          disabled={!twoFAToken || loginState.isVerifying2FA}
                        >
                          {loginState.isVerifying2FA ? (
                            <i className="gg-spinner-two"></i>
                          ) : (
                            'Verify'
                          )}
                        </button>
                      </div>
                    </>
                  )}

                  {/* login error */}
                  {loginState.loginError && (
                    <div className="transitionedInputWrapper">
                      <div
                        className={` 
                     ${
                       isResendVerifyVisible ? 'staticToast' : 'staticToast'
                     }  `}
                      >
                        <img
                          width="50"
                          src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/aniamted-error-icon-light.gif"
                          alt=""
                        />
                        {isResendVerifyVisible ? (
                          <p>Your email is pending verification.</p>
                        ) : (
                          <p>
                            {loginState.loginError}
                            {params.get('errorRedirectUrl') ? (
                              <span>
                                &nbsp; please click here to return to{'  '}
                                &nbsp;
                                <Link to={params.get('errorRedirectUrl') || ''}>
                                  Workspace
                                </Link>
                              </span>
                            ) : null}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </form>

                {isResendVerifyVisible && (
                  <div className="transitionedInputWrapper mb-17">
                    <p className="mb-10">
                      Please click the verification link we sent to your email.
                    </p>
                    <button
                      onClick={() => {
                        setIsResendVerifyVisible(false);
                        actions.resetLoginError();
                        onResendVerify();
                      }}
                      className={`full-width mb-17  large-btn bold' 
                                 ${
                                   loginState.isResendingVerifyLink
                                     ? 'roundSpinnerButton'
                                     : ''
                                 }
                                      ${
                                        loginState.resendVerifyLinkSuccess
                                          ? 'successImage'
                                          : 'blueButton'
                                      }
                                  `}
                      disabled={loginState.isResendingVerifyLink}
                    >
                      {loginState.isResendingVerifyLink ? (
                        <i className="gg-spinner-two"></i>
                      ) : (
                        <>Resend verification email</>
                      )}
                    </button>
                  </div>
                )}
                {isForgotPasswordFormVisible && (
                  <div className="transitionedInputWrapper mb-17">
                    <div className="subTitle">
                      Please enter your email address below. If your email is
                      associated with an account and has been activated, you
                      will receive a Reset Password link.
                    </div>
                    <form
                      className="d-flex flex-column login-form"
                      onSubmit={requestPasswordReset}
                    >
                      <div className="transitionedInputWrapper">
                        <label className="inp">
                          <input
                            required
                            value={email}
                            type="email"
                            onChange={e => updateEmail(e.target.value)}
                            placeholder="&nbsp;"
                            disabled={isAppsumoEmail}
                            tabIndex={1}
                          />
                          <span className="label">Email</span>
                          <span className="focus-bg"></span>
                        </label>
                      </div>

                      <div className="transitionedInputWrapper d-flex justify-content-center align-items-center">
                        <button
                          type="submit"
                          tabIndex={2}
                          className={`full-width mb-17 mt-10 primaryButton large-btn bold' ${
                            loginState.isRequestingPasswordReset
                              ? 'roundSpinnerButton'
                              : ''
                          }`}
                          onClick={() => {
                            setIsResendVerifyVisible(false);
                            actions.resetLoginError();
                          }}
                          disabled={
                            loginState.isRequestingPasswordReset ||
                            email === '' ||
                            !validEmail
                          }
                        >
                          {!loginState.isRequestingPasswordReset ? (
                            'Submit'
                          ) : (
                            <i className="gg-spinner-two"></i>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                {loginState.resendVerifyLinkError && (
                  <div className="transitionedInputWrapper mb-17">
                    <div className="staticToast">
                      <img
                        width="50"
                        src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/aniamted-error-icon-light.gif"
                        alt=""
                      />
                      {loginState.resendVerifyLinkError}
                    </div>
                  </div>
                )}

                {passwordResetSent && (
                  <div className="transitionedInputWrapper mb-17">
                    <div className="password-reset-success">
                      <i className="fi fi-rr-check-circle"></i>
                      <p>
                        If your email is associated with an account, a password
                        reset link has been sent.
                      </p>
                    </div>
                  </div>
                )}

                {passwordResetSuccess && (
                  <div className="transitionedInputWrapper mb-17">
                    <div className="password-reset-success">
                      <i className="fi fi-rr-check-circle"></i>
                      <p>
                        Your password has been successfully updated. You can now
                        log in with your new password.
                      </p>
                    </div>
                  </div>
                )}

                {passwordResetExpired && (
                  <div className="transitionedInputWrapper mb-17">
                    <div className="password-reset-expired">
                      <i className="fi fi-rr-exclamation-triangle"></i>
                      <p>
                        Your password reset link has expired. Please request a
                        new one below.
                      </p>
                    </div>
                  </div>
                )}

                {show2FAStep && (
                  <div className="transitionedInputWrapper mb-0">
                    <div className="text-info mt-10">
                      <button
                        onClick={() => {
                          setUseBackupCode(!useBackupCode);
                          setTwoFAToken('');
                        }}
                        className="link-text"
                      >
                        {useBackupCode
                          ? 'Use authenticator app instead'
                          : 'Use backup code instead'}
                      </button>
                    </div>
                  </div>
                )}

                {/* need an account  */}
                {!verifiedUser &&
                  !isResendVerifyVisible &&
                  !securityLoginNeeded &&
                  !show2FAStep && (
                    <div className="transitionedInputWrapper mb-0">
                      <div className="text-info mt-20">
                        {!isResendVerifyVisible ? (
                          <>
                            Don't have an account?{' '}
                            <Link
                              to={routes.newAccount}
                              className="link-text underline-type-1"
                            >
                              Sign up for free
                            </Link>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setIsResendVerifyVisible(false);
                                actions.resetLoginError();
                              }}
                              className="link-text "
                            >
                              Finish
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                {!isForgotPasswordFormVisible &&
                  !isResendVerifyVisible &&
                  !show2FAStep && (
                    <div className="transitionedInputWrapper mb-0">
                      <div className="text-info mt-10">
                        Trouble logging in?{' '}
                        <button
                          onClick={() => {
                            setIsForgotPasswordFormVisible(true);
                            setIsResendVerifyVisible(false);
                            actions.resetLoginError();
                          }}
                          className="link-text "
                        >
                          Request new password
                        </button>
                      </div>
                    </div>
                  )}
              </>
            )}
            <div className="reg-footer mt-20">
              <div className="reg-copyright">
                ©{new Date().getFullYear()}{' '}
                <a href="https://www.contracthub.com" target="_blank" rel="noreferrer">
                  ContractHub, Inc.
                </a>{' '}
                All rights reserved.
              </div>
            </div>
          </div>

          {/* <Link to={routes.forgotPassword} className="link-text">
        Forgot password
      </Link> */}
        </div>
      </div>
    </div>
  );
}
